import { BusinessObjectBase } from './base';
import type { Token } from './token';

export interface TimerData {
  readonly id: string;
  readonly boundaryEventId: string;
  readonly interrupting?: boolean;
  readonly fireAt?: Date;
  readonly status?: string;
  readonly tokenId: number;
  readonly token?: Token;
}

export interface Timer extends Readonly<TimerData> {}
export class Timer extends BusinessObjectBase {
  static readonly entityName = 'Timer' as const;
  static readonly plural = 'timers';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Timer[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Timer(data));
  }

  static async findOne(filter?: any): Promise<Timer | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Timer(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<Timer> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Timer(data);
  }

  static async create(data: Omit<TimerData, 'id'>): Promise<Timer> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Timer(result);
  }

  static async updateById(
    id: string,
    data: Partial<TimerData>,
  ): Promise<Timer> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new Timer(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async fire(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Timer.plural}/${(this as any).id}/fire`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
